import React, { useMemo, useState } from 'react';

import Loader from '../../../../../components/Loader';
import Modal from '../../../../../components/Model';
import InfoTable from '../../../../../components/ViewPage/InfoTable';
import CancelImg from '../../../../../images/SideBar/Cancel';
import { formatDate, formatDateCSV } from '../../../../../util/constant';
import { IProjectId } from '../../Project.props';
import useViewProject from './useViewProject';

const ViewProject: React.FC<IProjectId> = ({ projectId, setMethod }) => {
  const { projectData, loading } = useViewProject(projectId);
  const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null);
  const [dateRange, setDateRange] = useState({
    from: "",
    to: "",
  });

  const formatHours = (hours: number) => {
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    return `${wholeHours} hours ${minutes} minutes`;
    
  };

  const formatHoursCSV = (hours : number) => {
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
  
    const paddedHours = String(wholeHours).padStart(2, '0');
    const paddedMinutes = String(minutes).padStart(2, '0');
  
    return `${paddedHours}:${paddedMinutes}`;
  };
  const downloadCSV = () => {
    if (!projectId?.users) return;

    const headers = [
      "Name",
      "Total Hours",
      "Project Name",
      "Date",
      "Hours",
      "Description",
    ];
    const csvData = projectId.users.flatMap(
      (user: {
        name: string;
        totalHours: number;
        dateWise: Array<{
          date: string;
          hours: { hours: number; description: string };
        }>;
      }) =>
        user.dateWise.map(
          (dateData: {
            date: string;
            hours: { hours: number; description: string };
          }) => [
            user.name,
            formatHoursCSV(user.totalHours),
            projectId.name,
            formatDate(dateData.date),
            formatHoursCSV(dateData.hours.hours),
            dateData.hours.description || "",
          ]
        )
    );

    const csvContent = [
      headers.join(","),
      ...csvData.map((row: string[]) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${projectId.name}_project_data.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const projectInfo = {
    "Project Name": projectId?.name,
    Description: projectId?.description,
    Gitfeld: projectId?.gitfeld ?? "—",
    "Total Hours": formatHours(projectId?.totalHours || 0),
    Status: projectId?.status,
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setDateRange((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEmployeeClick = (employee: any) => {
    setSelectedEmployee(employee);
    setIsEmployeeModalOpen(true);
    setDateRange({ from: "", to: "" }); // Reset date range when opening modal
  };

  const filteredDateWiseData = useMemo(() => {
    if (!selectedEmployee?.dateWise) return [];

    return selectedEmployee.dateWise.filter((item: any) => {
      const itemDate = new Date(item.date);
      const fromDate = dateRange.from ? new Date(dateRange.from) : null;
      const toDate = dateRange.to ? new Date(dateRange.to) : null;

      if (!fromDate && !toDate) return true;
      if (fromDate && !toDate) return itemDate >= fromDate;
      if (!fromDate && toDate) return itemDate <= toDate;
      return fromDate && toDate
        ? itemDate >= fromDate && itemDate <= toDate
        : true;
    });
  }, [selectedEmployee?.dateWise, dateRange]);

  const filteredTotalHours = useMemo(() => {
    return filteredDateWiseData.reduce(
      (total: number, item: any) => total + item.hours.hours,
      0
    );
  }, [filteredDateWiseData]);

  const handleDownloadCSV = () => {
    const headerRows = [
      ["Name:", selectedEmployee.name],
      ["Project Name:", projectId.name],
      ["From Date:", dateRange.from ? formatDateCSV(dateRange.from) : "-"],
      ["To Date:", dateRange.to ? formatDateCSV(dateRange.to) : "-"],
      ["Filtered Total Hours:", formatHoursCSV(filteredTotalHours)],
      [""], // Empty row for spacing
    ];
    const headers = ["Date", "Hours", "Description"];
    const csvData = filteredDateWiseData.map((dateData: any) => [
      formatDateCSV(dateData.date),
      formatHoursCSV(dateData.hours.hours),
      dateData.hours.description || "",
    ]);
    const csvContent = [
      ...headerRows.map((row) => row.join(",")),
      headers.join(","),
      ...csvData.map((row: string[]) => row.join(",")),
    ].join("\n");
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `Blisslers ${selectedEmployee.name}(${projectId.name}).csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadEmployeeCSV = (employee: any) => {
    const headerRows = [
      ["Name:", employee.name],
      ["Project Name:", projectId.name],
      ["Total Hours:", formatHoursCSV(employee.totalHours)],
      [""], // Empty row for spacing
    ];
    const headers = ["Date", "Hours", "Description"];
    const csvData = employee.dateWise.map((dateData: any) => [
      formatDateCSV(dateData.date),
      formatHoursCSV(dateData.hours.hours),
      dateData.hours.description || "",
    ]);
    const csvContent = [
      ...headerRows.map((row) => row.join(",")),
      headers.join(","),
      ...csvData.map((row: string[]) => row.join(",")),
    ].join("\n");
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `Blisslers ${employee.name}(${projectId.name}).csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="flex flex-col items-center w-full h-full pr-8 pl-6">
      {loading && <Loader />}
      <div className="bg-white shadow-md rounded-lg pt-6 pb-6 w-full">
        <div className="border-b-2 flex justify-between items-center px-6 py-4">
          <h1 className="text-3xl text-fontBlack">View Project</h1>
          <div className="flex gap-4">
            <button
              onClick={downloadCSV}
              className="bg-primary-2 text-black px-4 py-2 rounded-md hover:bg-primary-1 transition-colors"
            >
              Download CSV
            </button>
            <button onClick={() => setMethod?.()}>
              <CancelImg />
            </button>
          </div>
        </div>
        <div className="space-y-6 p-6 h-[70vh] overflow-y-auto bg-mainBg">
          <div className="flex bg-white rounded-xl">
            <div className="m-3 mr-5 mb-5 w-1/2">
              <InfoTable data={projectInfo} title="Project Info" />
            </div>
          </div>

          {/* Employees Section */}
          <div className="bg-white rounded-xl p-4">
            <h2 className="text-xl font-semibold mb-4">Employees</h2>
            <div className="space-y-4">
              {projectId?.users?.map((user: any, index: number) => (
                <div
                  key={index}
                  className="border rounded-lg p-4 hover:bg-gray-50"
                >
                  <div className="flex justify-between items-center">
                    <div 
                      className="flex-1 cursor-pointer"
                      onClick={() => handleEmployeeClick(user)}
                    >
                      <div>
                        <span className="font-medium">{user.name}</span>
                        <span className="text-gray-600 ml-2">({user.email})</span>
                      </div>
                      <span className="text-gray-600">
                        {formatHours(user.totalHours)}
                      </span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadEmployeeCSV(user);
                      }}
                      className="bg-primary-2 text-black px-3 py-1 rounded text-sm hover:bg-primary-1 transition-colors"
                    >
                      Download CSV
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Employee Details Modal */}
      <Modal
        isOpen={isEmployeeModalOpen}
        onClose={() => setIsEmployeeModalOpen(false)}
        style="w-[800px] max-h-[80vh]"
      >
        {selectedEmployee && (
          <div className="p-4">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold mb-4">
                {selectedEmployee.name}
              </h2>
              <button
                onClick={handleDownloadCSV}
                className="mt-2 mb-4 bg-primary-2 text-black px-4 py-2 rounded"
              >
                Download CSV
              </button>
            </div>

            <div className="mb-4">
              <span className="font-semibold">Total Hours: </span>
              {formatHours(selectedEmployee.totalHours)}
            </div>

            {/* Date Range Filters */}
            <div className="flex gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  From Date
                </label>
                <input
                  type="date"
                  name="from"
                  value={dateRange.from}
                  onChange={handleDateChange}
                  className="border rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  To Date
                </label>
                <input
                  type="date"
                  name="to"
                  value={dateRange.to}
                  onChange={handleDateChange}
                  className="border rounded-md px-3 py-2"
                />
              </div>
            </div>
            {/* Filtered Total Hours */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <span className="font-semibold">Filtered Total Hours: </span>
              <span className="text-primary-2">
                {formatHours(filteredTotalHours)}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border p-2 text-left">Date</th>
                    <th className="border p-2 text-left">Hours</th>
                    <th className="border p-2 text-left">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDateWiseData.map((dateData: any, index: number) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="border p-2">
                        {formatDate(dateData.date)}
                      </td>
                      <td className="border p-2">
                        {formatHours(dateData.hours.hours)}
                      </td>
                      <td className="border p-2">
                        {dateData.hours.description}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ViewProject;
